import Phaser from "phaser";
import {
  ARENA_COLS,
  ARENA_ROWS,
  Directions,
  FIRE_MANA_COST,
  FIRE_TILE_COUNT,
  HEAL_MANA_COST,
  MOVE_REPEAT_DELAY_MS,
  MOVE_REPEAT_RATE_MS,
  PLAYER1_SPAWN,
  PLAYER2_SPAWN,
  SPAWN_EXCLUSION_RADIUS,
  TILE_SIZE,
} from "../config";
import type { GameConfig } from "../index";
import { FireTile } from "../entities/FireTile";
import { Player, type WhichPlayer } from "../entities/Player";
import { Projectile } from "../entities/Projectile";
import { type InputBindings, type PlayerKeys, setupInput } from "../input";
import {
  ARENA_BG_COLOR,
  SPRITE_FRAMES,
  SPRITESHEET_KEY,
  SPRITESHEET_TILE,
} from "../sprites";

const DECORATION_SCALE = TILE_SIZE / SPRITESHEET_TILE;
const DECORATION_CHANCE = 0.18; // ~18% of floor tiles get a decoration

interface HeldKeyState {
  nextRepeatAt: number;
}

/**
 * Main game scene. Owns the players, fire tiles, projectiles, and the
 * input/collision/win-condition wiring that ties them together.
 *
 * Per-player state lives on the Player class; rules and coordination live
 * here. The split keeps Player reusable and the scene focused on "what
 * happens this frame".
 */
export class ArenaScene extends Phaser.Scene {
  player1!: Player;
  player2!: Player;

  private fireTilesByKey = new Map<string, FireTile>();
  private projectiles!: Phaser.Physics.Arcade.Group;
  private bindings!: InputBindings;
  private heldMove = new WeakMap<Phaser.Input.Keyboard.Key, HeldKeyState>();
  private isGameOver = false;
  private isPaused = false;
  private pauseOverlay?: Phaser.GameObjects.Container;

  constructor() {
    super("ArenaScene");
  }

  create(): void {
    this.isGameOver = false;
    this.isPaused = false;

    const config = this.registry.get("config") as GameConfig | undefined;
    if (!config) throw new Error("ArenaScene started without a game config");

    this.renderGround();
    this.spawnPlayers(config);
    this.spawnFireTiles();
    this.setupProjectilesAndCollisions();

    this.bindings = setupInput(this);

    this.input.keyboard?.on("keydown-ESC", () => this.togglePause());
    this.input.keyboard?.on("keydown-M", () => this.returnToMenu());

    this.scene.launch("HUDScene");

    // Make sure HUDScene tears down when this scene shuts down (e.g. on rematch).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scene.stop("HUDScene");
    });
  }

  override update(time: number): void {
    if (this.isGameOver || this.isPaused) return;

    this.handleMovement(this.player1, this.bindings.player1, time);
    this.handleMovement(this.player2, this.bindings.player2, time);
    this.handleActions(this.player1, this.bindings.player1);
    this.handleActions(this.player2, this.bindings.player2);
  }

  /* -------------------- setup -------------------- */

  private renderGround(): void {
    // Kenney's tiles are transparent on a solid background — set the camera
    // background first, then scatter sparse decorative tiles on top so the
    // arena floor reads as "outdoor terrain" rather than an empty grid.
    this.cameras.main.setBackgroundColor(ARENA_BG_COLOR);

    // Exclude player spawn zones from decoration scatter so the spawn squares
    // are clean and readable. Fire-tile spawn does the same via SPAWN_EXCLUSION_RADIUS.
    const excluded = new Set<string>();
    for (const spawn of [PLAYER1_SPAWN, PLAYER2_SPAWN]) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          excluded.add(`${spawn.col + dx},${spawn.row + dy}`);
        }
      }
    }

    for (let row = 0; row < ARENA_ROWS; row++) {
      for (let col = 0; col < ARENA_COLS; col++) {
        if (Math.random() > DECORATION_CHANCE) continue;
        if (excluded.has(`${col},${row}`)) continue;
        const frame =
          SPRITE_FRAMES.decorations[
            Math.floor(Math.random() * SPRITE_FRAMES.decorations.length)
          ];
        this.add
          .sprite(
            col * TILE_SIZE + TILE_SIZE / 2,
            row * TILE_SIZE + TILE_SIZE / 2,
            SPRITESHEET_KEY,
            frame,
          )
          .setScale(DECORATION_SCALE)
          .setAlpha(0.85)
          .setDepth(1);
      }
    }
  }

  private spawnPlayers(config: GameConfig): void {
    const canMoveTo =
      (which: WhichPlayer) =>
      (col: number, row: number): boolean => {
        const other = which === "player1" ? this.player2 : this.player1;
        if (!other) return true;
        if (!other.isAlive) return true;
        return !(other.col === col && other.row === row);
      };

    this.player1 = new Player(
      this,
      "player1",
      config.player1,
      PLAYER1_SPAWN,
      Directions.Right,
      canMoveTo("player1"),
    );
    this.player2 = new Player(
      this,
      "player2",
      config.player2,
      PLAYER2_SPAWN,
      Directions.Left,
      canMoveTo("player2"),
    );

    this.player1.on("died", () => this.endGame(this.player2));
    this.player2.on("died", () => this.endGame(this.player1));
    // Standing-on-fire damage is applied via onPlayerMoved() after each
    // successful tile transition (see handleMovement).
  }

  private spawnFireTiles(): void {
    // Build exclusion set around each spawn.
    const excluded = new Set<string>();
    for (const spawn of [PLAYER1_SPAWN, PLAYER2_SPAWN]) {
      for (let dy = -SPAWN_EXCLUSION_RADIUS; dy <= SPAWN_EXCLUSION_RADIUS; dy++) {
        for (let dx = -SPAWN_EXCLUSION_RADIUS; dx <= SPAWN_EXCLUSION_RADIUS; dx++) {
          excluded.add(`${spawn.col + dx},${spawn.row + dy}`);
        }
      }
    }

    let placed = 0;
    let attempts = 0;
    while (placed < FIRE_TILE_COUNT && attempts < FIRE_TILE_COUNT * 10) {
      attempts++;
      const col = Phaser.Math.Between(0, ARENA_COLS - 1);
      const row = Phaser.Math.Between(0, ARENA_ROWS - 1);
      const key = `${col},${row}`;
      if (excluded.has(key) || this.fireTilesByKey.has(key)) continue;
      this.fireTilesByKey.set(key, new FireTile(this, col, row));
      placed++;
    }
  }

  private setupProjectilesAndCollisions(): void {
    this.projectiles = this.physics.add.group();

    // physics.add.overlap(A, B, cb): cb is called as cb(A, B). Here A is the
    // projectile group (children passed individually) and B is the player sprite.
    this.physics.add.overlap(
      this.projectiles,
      this.player1.sprite,
      ((projObj, _playerSprite) =>
        this.onProjectileHit(projObj as Projectile, this.player1)) as ArcadeOverlapCallback,
      undefined,
      this,
    );
    this.physics.add.overlap(
      this.projectiles,
      this.player2.sprite,
      ((projObj, _playerSprite) =>
        this.onProjectileHit(projObj as Projectile, this.player2)) as ArcadeOverlapCallback,
      undefined,
      this,
    );
  }

  /* -------------------- per-frame input -------------------- */

  private handleMovement(
    player: Player,
    keys: PlayerKeys,
    time: number,
  ): void {
    const tryMove = (dx: number, dy: number, key: Phaser.Input.Keyboard.Key) => {
      if (Phaser.Input.Keyboard.JustDown(key)) {
        if (player.attemptMove(dx, dy)) this.onPlayerMoved(player);
        this.heldMove.set(key, { nextRepeatAt: time + MOVE_REPEAT_DELAY_MS });
        return;
      }
      const state = this.heldMove.get(key);
      if (key.isDown && state && time >= state.nextRepeatAt) {
        if (player.attemptMove(dx, dy)) this.onPlayerMoved(player);
        state.nextRepeatAt = time + MOVE_REPEAT_RATE_MS;
      } else if (!key.isDown) {
        this.heldMove.delete(key);
      }
    };

    tryMove(-1, 0, keys.moveLeft);
    tryMove(1, 0, keys.moveRight);
    tryMove(0, -1, keys.moveUp);
    tryMove(0, 1, keys.moveDown);
  }

  private handleActions(player: Player, keys: PlayerKeys): void {
    if (Phaser.Input.Keyboard.JustDown(keys.punch)) this.attemptPunch(player);
    if (Phaser.Input.Keyboard.JustDown(keys.fire)) this.attemptFire(player);
    if (Phaser.Input.Keyboard.JustDown(keys.heal)) this.attemptHeal(player);
  }

  /* -------------------- gameplay actions -------------------- */

  private onPlayerMoved(player: Player): void {
    const tile = this.fireTilesByKey.get(`${player.col},${player.row}`);
    if (tile) player.takeDamage(tile.damage);
  }

  private attemptPunch(attacker: Player): void {
    const target = attacker === this.player1 ? this.player2 : this.player1;
    if (!target.isAlive) return;
    if (!attacker.isAdjacentTo(target)) return;
    const damage = Phaser.Math.Between(1, attacker.fistMax);
    target.takeDamage(damage);
    this.cameras.main.shake(80, 0.003);
  }

  private attemptFire(caster: Player): void {
    if (!caster.spendMana(FIRE_MANA_COST)) return;
    const damage = caster.magicLevel * Phaser.Math.Between(10, 30);

    // Spawn just in front of the caster so projectile-vs-self overlap doesn't fire.
    const startCol = caster.col + caster.direction.x;
    const startRow = caster.row + caster.direction.y;

    const projectile = new Projectile(
      this,
      startCol,
      startRow,
      caster.direction,
      damage,
      caster.which,
    );
    this.projectiles.add(projectile);
  }

  private attemptHeal(caster: Player): void {
    if (!caster.spendMana(HEAL_MANA_COST)) return;
    const amount = Phaser.Math.Between(1, caster.healDie);
    caster.heal(amount);
  }

  private onProjectileHit(projectile: Projectile, victim: Player): void {
    if (!projectile.active) return;
    if (projectile.ownerWhich === victim.which) return;
    if (!victim.isAlive) return;
    victim.takeDamage(projectile.damage);
    projectile.destroy();
  }

  /* -------------------- pause + game over -------------------- */

  private togglePause(): void {
    if (this.isGameOver) return;
    this.isPaused = !this.isPaused;
    if (this.isPaused) {
      this.physics.pause();
      this.showPauseOverlay();
    } else {
      this.physics.resume();
      this.pauseOverlay?.destroy();
      this.pauseOverlay = undefined;
    }
  }

  private showPauseOverlay(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;
    const bg = this.add.rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0.65);
    const title = this.add.text(cx, cy - 40, "PAUSED", {
      fontFamily: "monospace",
      fontSize: "40px",
      color: "#f97316",
    }).setOrigin(0.5);
    const hint = this.add.text(cx, cy + 20, "Esc to resume   ·   M to leave the match", {
      fontFamily: "monospace",
      fontSize: "16px",
      color: "#e0e0e0",
    }).setOrigin(0.5);
    this.pauseOverlay = this.add.container(0, 0, [bg, title, hint]).setDepth(1000);
  }

  private endGame(winner: Player): void {
    if (this.isGameOver) return;
    this.isGameOver = true;
    this.physics.pause();
    this.time.delayedCall(700, () => {
      this.scene.start("GameOverScene", {
        winnerName: winner.name,
        winnerVocation: winner.vocation,
        winnerHp: winner.hp,
        winnerMaxHp: winner.maxHp,
      });
    });
  }

  private returnToMenu(): void {
    // Hard reload back to homepage state — same approach the placeholder used.
    // Phase 6+ could add a proper menu scene; reload keeps it simple.
    window.location.reload();
  }
}

// Phaser's arcade overlap callback type is awkward to spell inline.
type ArcadeOverlapCallback = Phaser.Types.Physics.Arcade.ArcadePhysicsCallback;
